import { SearchableSelect } from "@/components/molecules/searchable-select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useCallingCodes } from "@/features/connectors/hooks/use-connectors";
import type { OutboundCallFilter } from "@/features/connectors/types";
import { useTranslation } from "@/features/utils/i18n";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { localizedCountries } from "@/features/utils/display-names";

/**
 * Which countries this line may call **out** to.
 *
 * The mirror of the geographic filter above, and the asymmetry between them is the
 * point: that one refuses calls and costs a missed customer, this one places them and
 * costs money. A caller from abroad leaves a number, the agent promises to call back,
 * and the leg goes out on the tenant's bill — so the line's own country is reachable
 * with nothing configured, and everything else is said explicitly here.
 *
 * It is the tenant's own setting, not platform staff's: they are the ones paying, and
 * they are the ones who know whether they call Belgium.
 */
export const OutboundFilterField = ({
  value,
  onChange,
}: {
  value: OutboundCallFilter;
  onChange: (filter: OutboundCallFilter) => void;
}) => {
  const { t, i18n } = useTranslation("connectors");
  const { company: clientId } = useParams<{ company: string }>();
  const { data: callingCodes = [] } = useCallingCodes(clientId || "");

  // "+32 — Belgique". The code is part of the label because it is what is stored and
  // what a refusal names back: a tenant reading "allow +32" must find it here.
  const items = useMemo(() => {
    const countryNames = new Map(
      localizedCountries(i18n.language).map((c) => [c.code, c.name]),
    );
    return callingCodes
      .map((entry) => ({
        id: entry.code,
        label: `${entry.code} — ${countryNames.get(entry.country) || entry.country}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, i18n.language));
  }, [callingCodes, i18n.language]);

  const update = (changes: Partial<OutboundCallFilter>) =>
    onChange({ ...value, ...changes });

  return (
    <div className="space-y-2 border-t pt-3">
      <Label className="text-sm font-medium">
        {t("connectors.outbound.title", "Appels sortants")}
      </Label>
      <p className="text-xs text-muted-foreground">
        {t(
          "connectors.outbound.description",
          "Le pays du numéro de cette ligne est toujours joignable. Ajoutez ici les autres pays que l'agent a le droit d'appeler — rappels promis en fin d'appel, relances, transferts, API.",
        )}
      </p>

      <div className="space-y-3 pt-1">
        <div className="space-y-1">
          <Label className="text-xs">
            {t("connectors.outbound.countriesLabel", "Pays supplémentaires")}
          </Label>
          <SearchableSelect
            multiple
            showBadges
            items={items}
            disabled={value.allow_all === true}
            value={value.prefixes}
            onChange={(prefixes) =>
              update({
                prefixes: Array.isArray(prefixes)
                  ? prefixes
                  : [prefixes].filter(Boolean),
              })
            }
            placeholder={t(
              "connectors.outbound.countriesPlaceholder",
              "Aucun — le pays de la ligne uniquement",
            )}
            searchPlaceholder={t(
              "connectors.outbound.countriesSearch",
              "Rechercher un pays ou un indicatif…",
            )}
            popoverWidth="w-full"
          />
        </div>

        <div className="flex items-start space-x-2">
          <Checkbox
            id="outbound-allow-all"
            checked={value.allow_all === true}
            onCheckedChange={(checked) =>
              update({ allow_all: checked === true })
            }
          />
          <div className="space-y-1">
            <Label
              htmlFor="outbound-allow-all"
              className="text-sm font-normal cursor-pointer"
            >
              {t(
                "connectors.outbound.allowAll.label",
                "Autoriser tous les pays",
              )}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t(
                "connectors.outbound.allowAll.description",
                "À n'activer que si votre activité est réellement internationale : plus rien ne limite le coût d'un rappel vers l'étranger.",
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
